import React, { useState, useEffect, useRef } from "react";
import { Button, Table, Typography } from "antd";
import { CaretDownOutlined, CaretUpOutlined } from "@ant-design/icons";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import Pageloader from "../../../components/loader";
import { validationErrors } from "../../../utils/Helpers";
import * as workActions from "../../../redux/actions/work.actions";
import ApprovalHistoryFilters from "./filters";
import moment from "moment";

const { Title } = Typography;

const formatMoney = (value) =>
  value !== null && value !== undefined
    ? new Intl.NumberFormat("en-PK", {
        style: "currency",
        currency: "PKR",
        minimumFractionDigits: 0,
      }).format(value)
    : "-";

const formatValue = (value) => (value === null || value === undefined || value === "" ? "-" : value);

const itemColumns = [
  {
    title: "SKU",
    dataIndex: "itemSku",
    key: "itemSku",
    render: formatValue,
  },
  {
    title: "Product",
    dataIndex: "itemName",
    key: "itemName",
    render: formatValue,
  },
  {
    title: "Ordered Qty",
    dataIndex: "orderedQty",
    key: "orderedQty",
    render: formatValue,
  },
  {
    title: "Picked Qty",
    dataIndex: "pickedQty",
    key: "pickedQty",
    render: formatValue,
  },
  {
    title: "Short Qty",
    dataIndex: "shortQty",
    key: "shortQty",
    render: formatValue,
  },
  {
    title: "Approver ID",
    dataIndex: "approverUserId",
    key: "approverUserId",
    render: formatValue,
  },
  {
    title: "Approver Name",
    dataIndex: "approverUserName",
    key: "approverUserName",
    render: formatValue,
  },
  {
    title: "Approver Email",
    dataIndex: "approverUserEmail",
    key: "approverUserEmail",
    render: formatValue,
  },
];

const ApprovalHistory = ({ workActions, approvalHistoryData, approvalHistoryPagination, zoneData }) => {
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageSize, setCurrentPageSize] = useState(10);
  const [filters, setFilters] = useState({
    orderCreationDate: [moment().format("YYYY-MM-DD"), moment().format("YYYY-MM-DD")],
  });
  const skipNextPageSizeEffectRef = useRef(false);

  const fetchList = async (extraFilters = {}, signal = null) => {
    try {
      setLoading(true);
      const finalFilters = {
        ...filters,
        ...extraFilters,
        page: extraFilters.page ?? currentPage,
        limit: extraFilters.limit ?? currentPageSize,
      };
      await workActions.getApprovalHistoryAction(finalFilters, signal);
      setFilters(finalFilters);
    } catch (error) {
      if (error?.name !== "Canceled" && error?.code !== "ERR_CANCELED") validationErrors(error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (value = {}) => {
    const nextPage = value.page ?? 1;
    const nextLimit = value.limit ?? currentPageSize;
    if (nextPage !== currentPage || nextLimit !== currentPageSize) {
      skipNextPageSizeEffectRef.current = true;
      setCurrentPage(nextPage);
      setCurrentPageSize(nextLimit);
    }
    fetchList({ ...value, page: nextPage, limit: nextLimit });
  };

  const storeData = zoneData?.reduce((acc, item) => {
    acc[String(item.storeId)] = item.zoneName;
    return acc;
  }, {}) || {};

  const handleExportCsv = async () => {
    try {
      setExportLoading(true);
      await workActions.exportApprovalHistoryAction(filters);
    } catch (error) {
      validationErrors(error);
    } finally {
      setExportLoading(false);
    }
  };

  useEffect(() => {
    if (skipNextPageSizeEffectRef.current) {
      skipNextPageSizeEffectRef.current = false;
      return;
    }
    const controller = new AbortController();
    fetchList({ page: currentPage, limit: currentPageSize }, controller.signal);
    return () => controller.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, currentPageSize]);

  const columns = [
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (value) =>
        value ? (
          <>
            <span>{moment(value).format("DD MMMM, YYYY")}</span>
            <p>{moment(value).format("h:mm A")}</p>
          </>
        ) : (
          "-"
        ),
    },
    {
      title: "Order ID",
      dataIndex: "orderId",
      key: "orderId",
      render: formatValue,
    },
    {
      title: "Zone",
      dataIndex: "storeId",
      key: "storeId",
      render: (storeId) => storeData[String(storeId)] || storeId || "-",
    },
    {
      title: "Invoice Before",
      dataIndex: "invoiceAmountBeforeAdjustment",
      key: "invoiceAmountBeforeAdjustment",
      render: formatMoney,
    },
    {
      title: "Invoice After",
      dataIndex: "invoiceAmountAfterAdjustment",
      key: "invoiceAmountAfterAdjustment",
      render: formatMoney,
    },
  ];

  const expandedRowRender = (record) => (
    <div style={{ marginTop: 16 }}>
      <Table
        columns={itemColumns}
        dataSource={record.items || []}
        pagination={false}
        rowKey={(item, index) => `${record._id}-${item.itemSku || index}`}
        size="small"
      />
    </div>
  );

  const defaultExpandedRowKeys = (approvalHistoryData || [])
    .filter((row) => Array.isArray(row.items) && row.items.length === 1)
    .map((row) => row._id);

  return (
    <div className="page-content">
      <div className="page-content-header" style={{ display: "flex", justifyContent: "space-between" }}>
        <Title level={2}>Approval History</Title>
      </div>
      <div className="p-content">
        <ApprovalHistoryFilters
          onFilterChange={handleFilterChange}
          onExportCsv={handleExportCsv}
          exportLoading={exportLoading}
        />
        {loading && !(approvalHistoryData || []).length ? (
          <Pageloader />
        ) : (
          <Table
            className="tablehover"
            dataSource={approvalHistoryData || []}
            columns={columns}
            rowKey="_id"
            loading={loading}
            expandable={{
              expandedRowRender,
              defaultExpandedRowKeys,
              rowExpandable: (record) => Array.isArray(record.items) && record.items.length > 0,
              expandIcon: ({ expanded, onExpand, record }) =>
                Array.isArray(record.items) && record.items.length > 0 ? (
                  <Button
                    type="text"
                    size="small"
                    icon={expanded ? <CaretUpOutlined /> : <CaretDownOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      onExpand(record, e);
                    }}
                  />
                ) : null,
            }}
            pagination={{
              total: Number(approvalHistoryPagination?.total) || 0,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
              current: currentPage,
              pageSize: currentPageSize,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "30", "50", "100"],
              onChange: (page, size) => {
                setCurrentPage(page);
                setCurrentPageSize(size);
              },
            }}
          />
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  approvalHistoryData: state.WorkReducer.approvalHistoryData,
  approvalHistoryPagination: state.WorkReducer.approvalHistoryPagination,
  zoneData: state.ZoneReducer.zoneData,
});

const mapDispatchToProps = (dispatch) => ({
  workActions: bindActionCreators(workActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(ApprovalHistory);
